import { useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import Icon, { type IconName } from './Icon';

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row gap-8 faint">
      <span className="spinner" />
      {label ? <span className="small">{label}</span> : null}
    </span>
  );
}

export function PageLoading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="col gap-12" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 56 }} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon, title, body, action,
}: { icon?: IconName; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon ? <Icon name={icon} size={30} strokeWidth={1.5} /> : null}
      <h3 style={{ marginTop: 10 }}>{title}</h3>
      {body ? <p className="small" style={{ maxWidth: 380, margin: '0 auto' }}>{body}</p> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </div>
  );
}

export function Banner({
  kind = 'info', children, icon = true,
}: { kind?: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode; icon?: boolean }) {
  const name: IconName = kind === 'info' ? 'info'
    : kind === 'success' ? 'check' : 'alert';
  return (
    <div className={`banner banner-${kind}`}>
      {icon ? <Icon name={name} size={17} /> : null}
      <div className="grow">{children}</div>
    </div>
  );
}

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps) {
  return (
    <div className="field">
      {label ? <label>{label}</label> : null}
      {children}
      {error ? <span className="field-error">{error}</span> : null}
      {!error && hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string; hint?: string; error?: string;
};

export function TextInput({ label, hint, error, className, ...rest }: TextInputProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      <input
        className={`input${error ? ' invalid' : ''}${className ? ` ${className}` : ''}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string; hint?: string; error?: string;
};

export function TextArea({ label, hint, error, className, ...rest }: TextAreaProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      <textarea
        className={`textarea${error ? ' invalid' : ''}${className ? ` ${className}` : ''}`}
        {...rest}
      />
    </Field>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string; hint?: string; error?: string; children: ReactNode;
};

export function Select({ label, hint, error, children, className, ...rest }: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error}>
      <select className={`select${error ? ' invalid' : ''}${className ? ` ${className}` : ''}`} {...rest}>
        {children}
      </select>
    </Field>
  );
}

export function Toggle({
  checked, onChange, label, disabled, testId,
}: {
  checked: boolean; onChange: (v: boolean) => void;
  label: string; disabled?: boolean; testId?: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch-track" />
      <span className="switch-label">{label}</span>
    </label>
  );
}

export function Badge({
  tone = 'neutral', children,
}: { tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info'; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function SearchInput({
  value, onChange, placeholder = 'Search',
}: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="search">
      <Icon name="search" size={16} />
      <input
        className="input"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * A pick-from-a-list-or-add-a-new-one field.
 *
 * The product form's category, brand and unit are managed masters, but a
 * rep-facing form should never be a dead end: if the value the admin wants
 * is not on the list yet, they type it and "Add" puts it on the list in the
 * same gesture. That is the whole reason this exists rather than a <select>.
 */
export function Combobox({
  label, hint, error, value, options, placeholder = 'Select', disabled,
  onChange, onCreate, allowCreate = true,
}: {
  label?: string; hint?: string; error?: string;
  value: string; options: string[]; placeholder?: string; disabled?: boolean;
  onChange: (v: string) => void;
  onCreate?: (name: string) => void | Promise<void>;
  allowCreate?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = options.filter((o) => o.toLowerCase().includes(q));
  const exact = options.some((o) => o.toLowerCase() === q);
  const showCreate = allowCreate && !!onCreate && q.length > 0 && !exact;

  function pick(v: string) {
    onChange(v);
    setQuery('');
    setOpen(false);
  }

  async function create() {
    const name = query.trim();
    if (!name) return;
    await onCreate?.(name);
    pick(name);
  }

  return (
    <Field label={label} hint={hint} error={error}>
      <div className="combo" ref={wrapRef}>
        <button
          type="button"
          className={`combo-value${error ? ' invalid' : ''}`}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={value ? '' : 'faint'}>{value || placeholder}</span>
          <Icon name="chevron-down" size={15} />
        </button>
        {open ? (
          <div className="combo-pop">
            <div className="combo-search">
              <input
                autoFocus
                className="input"
                value={query}
                placeholder="Type to search or add"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (showCreate) void create();
                    else if (filtered[0]) pick(filtered[0]);
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
              />
            </div>
            <div className="combo-list">
              {value ? (
                <button type="button" className="combo-opt faint" onClick={() => pick('')}>
                  Clear
                </button>
              ) : null}
              {filtered.map((o) => (
                <button
                  type="button"
                  key={o}
                  className={`combo-opt${o === value ? ' selected' : ''}`}
                  onClick={() => pick(o)}
                >
                  {o}
                </button>
              ))}
              {showCreate ? (
                <button type="button" className="combo-opt combo-create" onClick={() => void create()}>
                  <Icon name="plus" size={14} /> Add “{query.trim()}”
                </button>
              ) : null}
              {filtered.length === 0 && !showCreate ? (
                <div className="combo-empty small faint">No matches</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Field>
  );
}

/** Buttons show a spinner in place of their label so the row never jumps. */
export function SubmitButton({
  busy, children, className = 'btn btn-primary', ...rest
}: InputHTMLAttributes<HTMLButtonElement> & { busy?: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      className={className}
      disabled={busy || rest.disabled}
      onClick={rest.onClick as never}
    >
      {busy ? <span className="spinner" /> : null}
      {children}
    </button>
  );
}
